/**
 * Generic permission guard for non-app routes (e.g. /users, /settings/*).
 *
 * Two ways to state the requirement:
 *
 *  - `module` — the Hub module's admin gate (or its legacy `app:*:access` slug).
 *    This is what Settings and the other module routes use.
 *  - `permission` — an explicit permission slug, for a surface whose read gate is
 *    a named permission rather than the generic module gate. `/users` uses this
 *    with `users:read`, which is the same string the server enforces on the
 *    membership routes; reaching the page through `app:users:access` (a slug
 *    whose name says "admin") was the wrong read gate even though it worked.
 *
 * The guard decides whether the *page* opens. Whether an action inside it is
 * offered is the page's own decision (`users:admin`), and the server enforces
 * both regardless — hiding an action is not authorization.
 */

import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { hasModuleAdminAccess } from '@/lib/module-permissions';

interface PermissionProtectedRouteProps {
  children: ReactNode;
  /** Hub module slug; accepts the module admin gate or the legacy app:* permission. */
  module?: string;
  /** Explicit permission slug to require instead of a module gate. */
  permission?: string;
  description?: string;
}

export function PermissionProtectedRoute({ children, module, permission, description }: PermissionProtectedRouteProps) {
  const { hasPermission, isLoading } = useUserPermissions();
  const navigate = useNavigate();

  if (isLoading) {
    return <Loader text="Checking permissions..." />;
  }

  const allowed = permission ? hasPermission(permission) : module ? hasModuleAdminAccess(hasPermission, module) : false;

  if (!allowed) {
    return (
      <div className="flex h-(--page-main-height) items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="error">
                <Lock />
              </EmptyMedia>
              <EmptyTitle>Access denied</EmptyTitle>
              {description && <EmptyDescription>{description}</EmptyDescription>}
              <Button variant="default" className="mt-4" onClick={() => navigate('/apps')}>
                Back to apps
              </Button>
            </EmptyHeader>
          </Empty>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
